import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { experienceContent } from "@/data/experience-content";
import { destinationContent } from "@/data/destination-content";
import { toolPages } from "@/data/tool-content";
import { useSiteLocale } from "./useSiteLocale";
import type { Locale } from "@/types";
import { getPageSeo } from "@/data/page-seo";
import { SEO_OWNER_ATTRIBUTE, seoHeadElements } from "@/lib/seo-head";

function clearPageSeo() {
  document.head.querySelectorAll(`[${SEO_OWNER_ATTRIBUTE}], [id^="image-tool-"]`).forEach((element) => element.remove());
}

const DEFAULT_DESCRIPTION = "Find winning video signals, understand why they work, and turn them into scripts your brand can shoot.";
const STATIC_META: Record<string, { title: string; description?: string }> = {
  "/early-access": { title: "Request early access | Zesrin", description: "Tell Zesrin about your brand and the creative workflow you want to improve." },
  "/integrations": { title: "Product integrations | Zesrin", description: "The planned connections for bringing evidence, assets, and approvals into Zesrin." },
  "/pricing": { title: "Pricing | Zesrin", description: "Early-access pricing principles for Zesrin." },
  "/releases": { title: "Zesrin release notes", description: "A record of Zesrin product updates and improvements." },
};

async function getDynamicMeta(pathname: string, search: string, locale: Locale) {
  const [, section, slug] = pathname.split("/");
  if (!slug) return undefined;
  if (section === "agents") {
    const item = experienceContent[locale].agents.find((agent) => agent.id === slug);
    const profile = destinationContent[locale].agents.profiles.find((agent) => agent.id === slug);
    return item && profile ? { title: `${item.label} | Zesrin`, description: profile.promise } : undefined;
  }
  if (section === "blog") {
    const { getBlogPost } = await import("@/data/blog-content");
    const requestedLocale = new URLSearchParams(search).get("lang");
    const locale = requestedLocale === "en" || requestedLocale === "zh" ? requestedLocale : "zh";
    const item = getBlogPost(slug, locale);
    return item ? {
      title: item.seo.title || `${item.title} | Zesrin`,
      description: item.seo.description || item.excerpt,
    } : undefined;
  }
  if (section === "tools") {
    const item = toolPages.find((tool) => tool.id === slug);
    return item ? { title: `${item.label} | Zesrin`, description: item.description } : undefined;
  }
  if (section === "use-cases") {
    const item = experienceContent[locale].useCases.find((useCase) => useCase.slug === slug);
    return item ? { title: `${item.title} | Zesrin`, description: item.body } : undefined;
  }
  return undefined;
}

export function RouteMetadata() {
  const { pathname, search } = useLocation();
  const { locale } = useSiteLocale();
  useEffect(() => {
    let cancelled = false;
    async function update() {
      // Run after locale-provider effects; directory/preview bodies may be English-only.
      await Promise.resolve();
      if (cancelled) return;
      clearPageSeo();
      const pageSeo = getPageSeo(pathname, locale, import.meta.env.MODE, import.meta.env.VITE_SITE_URL);
      if (pageSeo) {
        document.title = pageSeo.title;
        document.documentElement.lang = pageSeo.locale === "zh" ? "zh-CN" : "en";
        const description = document.querySelector<HTMLMetaElement>('meta[name="description"]') ?? document.createElement("meta");
        description.name = "description";
        description.content = pageSeo.description;
        if (!description.isConnected) document.head.append(description);
        for (const { tag, attributes, text } of seoHeadElements(pageSeo)) {
          const element = document.createElement(tag);
          element.setAttribute(SEO_OWNER_ATTRIBUTE, "true");
          for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
          if (text) element.textContent = text;
          document.head.append(element);
        }
        return;
      }
      // Unlisted pages, especially Blog articles, retain their original SEO rules.
      const integrationMeta = pathname === "/integrations"
        ? { title: locale === "zh" ? "集成规划 | Zesrin" : "Integration roadmap | Zesrin", description: destinationContent[locale].integrations.description }
        : undefined;
      const metadata = integrationMeta ?? STATIC_META[pathname] ?? await getDynamicMeta(pathname, search, locale) ?? { title: "Page not found | Zesrin", description: DEFAULT_DESCRIPTION };
      if (cancelled) return;
      document.title = metadata.title;
      document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", metadata.description ?? DEFAULT_DESCRIPTION);
    }
    // Route content has its own error boundary if its chunk cannot be fetched.
    void update().catch(() => undefined);
    return () => { cancelled = true; clearPageSeo(); };
  }, [locale, pathname, search]);
  return null;
}
